import { requisicaoAutenticada } from './http';

/** Os modelos gerais de contrato (D-020) — espelha `apps/api/src/modules/contratos`. */

export type UsoDoModelo =
  'CONTRATO_RECORRENTE' | 'CONTRATO_PONTUAL' | 'CONTRATO_PASSIVO' | 'TERMO_COMISSIONAMENTO';

export interface VariavelDoModelo {
  readonly token: string;
  readonly descricao: string;
  readonly exemplo: string;
}

export interface UsoComVariaveis {
  readonly id: UsoDoModelo;
  readonly rotulo: string;
  readonly descricao: string;
  /** `false` enquanto o Deca ainda não gera esse documento. */
  readonly geradoPeloDeca: boolean;
  readonly esqueleto: string | null;
  readonly grupos: readonly {
    readonly titulo: string;
    readonly variaveis: readonly VariavelDoModelo[];
  }[];
}

export interface ModeloGeralResumido {
  readonly id: string;
  readonly nome: string;
  readonly uso: UsoDoModelo;
  readonly arquivoOrigem: string | null;
  readonly temConteudo: boolean;
  readonly atualizadoEm: string;
}

export interface ModeloGeral {
  readonly id: string;
  readonly nome: string;
  readonly uso: UsoDoModelo;
  readonly arquivoOrigem: string | null;
  readonly conteudo: string;
  readonly atualizadoEm: string;
}

export interface ModelosGerais {
  readonly modelos: readonly ModeloGeralResumido[];
  readonly usos: readonly UsoComVariaveis[];
}

/**
 * Os `[TOKENS]` escritos que o uso não conhece — o gerador os apaga do PDF
 * (RN-043). A mesma regra de `tokensDesconhecidos`, no domínio.
 */
export function tokensDesconhecidos(conteudo: string, uso: UsoComVariaveis): string[] {
  const conhecidos = new Set(uso.grupos.flatMap((g) => g.variaveis.map((v) => v.token)));
  const achados = [...conteudo.matchAll(/\[([A-Z][A-Z0-9_]*)\]/g)].map((m) => m[1] ?? '');
  return [...new Set(achados)].filter((t) => t !== '' && !conhecidos.has(t));
}

/** O texto com cada variável trocada pelo exemplo, destacado — para a prévia. */
export function comExemplos(conteudo: string, uso: UsoComVariaveis): string {
  const exemplos = new Map(
    uso.grupos.flatMap((g) => g.variaveis.map((v) => [v.token, v.exemplo] as const)),
  );
  return conteudo.replace(/\[([A-Z][A-Z0-9_]*)\]/g, (inteiro, token: string) => {
    const exemplo = exemplos.get(token);
    return exemplo === undefined
      ? `<mark style="background:#fde2e2">${inteiro}</mark>`
      : `<mark style="background:#e6ebff">${escapar(exemplo)}</mark>`;
  });
}

function escapar(texto: string): string {
  return texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/* ─── Variáveis criadas (D-022) ───────────────────────────────────────────── */

export type FonteDaVariavel =
  | { readonly tipo: 'PARTE'; readonly parte: string; readonly campo: string }
  | { readonly tipo: 'NEGOCIACAO'; readonly campo: string };

export interface OpcaoDeFonte {
  readonly fonte: FonteDaVariavel;
  readonly rotulo: string;
  readonly exemplo: string;
}

export interface VariavelReconhecida {
  readonly token: string;
  /** Já foi criada antes, por outro modelo: não precisa perguntar. */
  readonly jaExiste: boolean;
  readonly provavel: OpcaoDeFonte | null;
  readonly sugestoes: readonly OpcaoDeFonte[];
}

export const reconhecerVariaveis = (
  uso: UsoDoModelo,
  tokens: readonly string[],
): Promise<{ variaveis: VariavelReconhecida[]; todas: OpcaoDeFonte[] }> =>
  requisicaoAutenticada('/contratos/variaveis/reconhecimento', {
    metodo: 'POST',
    corpo: { uso, tokens },
  });

export const criarVariaveis = (
  variaveis: readonly { readonly token: string; readonly fonte: FonteDaVariavel }[],
): Promise<unknown> =>
  requisicaoAutenticada('/contratos/variaveis', { metodo: 'POST', corpo: { variaveis } });

/** A mesma fonte? Para marcar a opção escolhida na janela. */
export function mesmaFonte(a: FonteDaVariavel, b: FonteDaVariavel): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
